/**
 * Speech and looking — the two rules M0-06 installs, and the two principles
 * they exist to establish.
 *
 * **Words are in the world.** There is no global chat channel here and there
 * never will be (design spec §8): a `say` is an event at a coordinate with a
 * radius, and whether you heard it is a question about where you were
 * standing. That is why the range lives in the sim next to the rule rather
 * than in the server next to the socket — "twelve tiles" is a fact about the
 * world, and a transport that decided it independently would be a second rules
 * engine nobody was maintaining. `yell` (×3 range) is the same handler with a
 * different constant when M1 wants it.
 *
 * **Looking is free and always answers.** `examine` costs nothing, has no
 * cooldown, and never returns nothing — every tile and every entity composes a
 * description (`describe.ts`). The plumbing is the deliverable: wounds, herbs,
 * tracks and trophies all arrive later as more lines from the same function.
 */
import { chebyshev } from './actor.js';
import { parseExamine, parseGesture, parseSay, MAX_SAY_LENGTH, } from './actions.js';
import { createLore } from './item.js';
import { describeAt } from './describe.js';
import { BEARINGS, MAX_TRACKING_FAMILIARITY } from './sign.js';
import { isHelpless } from './fighter.js';
import { inBounds } from './map.js';
import { silenced } from './marks.js';
import { MILLITICKS_PER_TICK } from './movement.js';
import { canSee, sightRadiusOf } from './sky.js';
/**
 * How far a spoken line carries, in tiles (Chebyshev — the same metric
 * movement and area-of-interest use, because a diagonal is one step).
 *
 * Twelve is a quarter of the viewport's width: close enough that hearing
 * somebody means you can probably see them, far enough that a conversation
 * survives one of you stepping away.
 */
export const SAY_RANGE = 12;
/**
 * Minimum ticks between two lines from the same mouth — one real second.
 *
 * Rate limiting belongs *in the sim* rather than in the room for the reason
 * everything else does: it is a rule, so it has to be in the replay. A limiter
 * in the server means a recorded session replays into a world where the flood
 * went through, and the recording is then not a recording of what happened.
 * It also means the limit is uniform across every future way of speaking.
 *
 * The room's `MAX_PENDING_PER_CLIENT` is the other half — that stops a flood
 * consuming server memory; this stops it reaching anyone's log.
 */
export const SAY_COOLDOWN_TICKS = 10;
const SAY_COST = SAY_COOLDOWN_TICKS * MILLITICKS_PER_TICK;
/** Milliticks elapsed at the start of `tick`. Mirrors `movement.ts`. */
function now(tick) {
    return tick * MILLITICKS_PER_TICK;
}
/** Has this actor's speech cooldown elapsed by `tick`? */
export function canSayAt(actor, tick) {
    return now(tick) >= actor.sayReadyAt;
}
/** Ticks until this actor may speak again — 0 if it may speak now. */
export function ticksUntilSay(actor, tick) {
    return Math.max(0, Math.ceil((actor.sayReadyAt - now(tick)) / MILLITICKS_PER_TICK));
}
/**
 * Whether `listener` can hear something said at (x, y).
 *
 * Takes a bare point rather than an `Actor` so the server can ask it of a
 * remembered position, and so the client can ask it of a predicted one.
 */
export function withinEarshot(listener, x, y, range = SAY_RANGE) {
    return chebyshev(listener.x, listener.y, x, y) <= range;
}
/* ------------------------------------------------------------------------- */
export const sayHandler = {
    type: 'say',
    // `parseSay` sanitizes and caps at MAX_SAY_LENGTH. The cap is enforced there
    // rather than here because it is a statement about the wire, not about the
    // world — a 10,000-character line is refused before it is ever a game
    // question.
    parse: parseSay,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return `${intent.actorId} is not in the world`;
        // The senseless do not talk (M2-07). `examine` deliberately stays legal
        // below: a player bleeding out on the ground can still *look* at what is
        // standing over them, and taking that away would be a black screen rather
        // than a mechanic — the last thing you see should be the worst part.
        const combatant = ctx.world.combat[intent.actorId];
        if (combatant && isHelpless(ctx.catalogue, combatant, ctx.tick)) {
            return `${actor.name} cannot speak`;
        }
        // The cauldron's mute (M4-07). Refused at the door like every other rule,
        // and refused with the *reason*, because a player whose key stopped working
        // has to be told why — the mark is a mechanic, not a bug.
        if (combatant && silenced(ctx.catalogue, combatant.vitals)) {
            return 'the words come up and stop; you cannot speak';
        }
        if (!canSayAt(actor, ctx.tick)) {
            return `too fast: ${ticksUntilSay(actor, ctx.tick)} tick(s) before speaking again`;
        }
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        // No carry, unlike movement: banking a minute of silence into a burst of
        // six lines is exactly the behaviour the limit exists to stop.
        actor.sayReadyAt = now(ctx.tick) + SAY_COST;
        const text = intent.payload.text.slice(0, MAX_SAY_LENGTH);
        ctx.emit({
            type: 'said',
            actors: [actor.id],
            refs: [actor.name, text],
            text: `${actor.name} says, "${text}"`,
            audience: 'earshot',
            at: { x: actor.x, y: actor.y },
            range: SAY_RANGE,
        });
    },
};
export const examineHandler = {
    type: 'examine',
    parse: parseExamine,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return `${intent.actorId} is not in the world`;
        const { x, y } = intent.payload;
        if (!inBounds(ctx.world.map, x, y))
            return `(${x}, ${y}) is off the map`;
        // Look follows sight, not speech. The viewport is wider than earshot, and
        // silently discarding questions about visible threats in that outer band
        // made the Look control fail exactly when it mattered. This is the same
        // radius and line-of-sight rule the snapshot uses to decide whether an
        // actor may be sent to this viewer, so examining can reveal nothing new.
        const sight = sightRadiusOf(ctx.world, ctx.catalogue, actor.id, ctx.tick);
        if (!canSee(ctx.world, actor, x, y, sight))
            return `(${x}, ${y}) cannot be seen from here`;
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        const { x, y } = intent.payload;
        const description = describeAt(ctx.world, x, y, actor.id, ctx.catalogue);
        // Reading the ground is how you get better at reading the ground (M3-08).
        //
        // Earned by examining a mark rather than by walking past one, which is the
        // same rule foraging follows: practice is a thing you did, not a thing that
        // happened near you. Capped, invisible, and never announced — you simply
        // notice one day that a trail is telling you more than it used to.
        if (description.refs.includes('sign'))
            practiseTracking(ctx.world, actor.id);
        ctx.emit({
            type: 'examined',
            actors: [actor.id],
            refs: description.refs,
            text: description.text,
            // The answer to a question only one person asked. Broadcasting it would
            // tell everyone nearby what you were looking at, which is information
            // the game never intended to give away.
            audience: 'actor',
            at: { x, y },
            to: actor.id,
        });
    },
};
/**
 * One sighting's worth of practice, capped.
 *
 * Writes into `world.lore` because that is where the live copy lives; the
 * durable one is on the character record, carried in and out by the session
 * exactly as the forage counter is.
 */
function practiseTracking(world, actorId) {
    const lore = (world.lore[actorId] ??= createLore());
    lore.tracking = Math.min(MAX_TRACKING_FAMILIARITY, (lore.tracking ?? 0) + 1);
}
/* -------------------------------------------------------------------------- *
 * Gestures (M4-07)
 * -------------------------------------------------------------------------- */
/**
 * How far a gesture carries.
 *
 * Further than a shout, and that is not a mistake: you can see an arm thrown
 * out across a clearing you could not make yourself heard across. It is also
 * the one small mercy the mute mark offers back, and it costs nothing to give.
 */
export const GESTURE_RANGE = 16;
/** What each one reads as, per direction. Prose, because it is all prose. */
const GESTURE_LINE = {
    point: (name, where) => `${name} points${where}.`,
    beckon: (name) => `${name} beckons, sharply.`,
    warn: (name, where) => `${name} throws out an arm in warning${where}.`,
};
/**
 * Say it without words.
 *
 * Available to everybody, always — including the senseless? No: a body that
 * cannot speak because it is unconscious cannot wave either, and the same check
 * `say` makes is the right one. What is *not* checked is the mute mark, which
 * is the entire point of the verb.
 */
export const gestureHandler = {
    type: 'gesture',
    parse: parseGesture,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return `${intent.actorId} is not in the world`;
        const combatant = ctx.world.combat[intent.actorId];
        if (combatant && isHelpless(ctx.catalogue, combatant, ctx.tick)) {
            return `${actor.name} cannot move`;
        }
        // The same throttle speech has, and for the same reason: a gesture spammed
        // forty times a second is a log nobody can read.
        if (!canSayAt(actor, ctx.tick)) {
            return `too fast: ${ticksUntilSay(actor, ctx.tick)} tick(s) before gesturing again`;
        }
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        actor.sayReadyAt = now(ctx.tick) + SAY_COST;
        const direction = intent.payload.direction;
        if (direction)
            actor.facing = direction;
        const where = direction ? ` ${BEARINGS[direction] ?? direction}` : '';
        ctx.emit({
            type: 'said',
            actors: [actor.id],
            refs: ['gesture', intent.payload.kind, direction ?? ''],
            text: GESTURE_LINE[intent.payload.kind](actor.name, where),
            // Seen, not heard — but delivered by the same range rule, because "who is
            // near enough to notice" is one question with one answer.
            audience: 'earshot',
            range: GESTURE_RANGE,
            at: { x: actor.x, y: actor.y },
        });
    },
};
/** Everything M0-06 teaches the world to do, plus M4-07's silent half. */
export const SPEECH_INTENTS = [
    sayHandler,
    examineHandler,
    gestureHandler,
];
//# sourceMappingURL=speech.js.map